import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Form, Tooltip, OverlayTrigger, ButtonGroup, Dropdown, Modal, Alert } from 'react-bootstrap'
import { issuedInventoryAPI } from '../api/issuedInventoryAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { issueDestinationAPI } from '../api/issueDestinationAPI'
import { formatDate, formatDateTime, highlightText, formatCurrency, formatQuantity } from '../utils/commonUtils'
import PaginationComponent from '../components/Pagination'
import StockItemInfo from '../components/StockItemInfo'
import { createColumnToggleDropdown } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import DateFilterDropdown from '../components/DateFilterDropdown'
import Select from 'react-select'
import '../styles/selectControl.css'
import SearchInputWithClear from '../components/SearchInputWithClear'

function IssuedInventoryListScreen() {
    const navigate = useNavigate()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    
    // State variables
    const [issuedInventoryItems, setIssuedInventoryItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '')
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
    const [totalPages, setTotalPages] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || PaginationComponent.getCurrentPageSize())
    const [shouldFetchData, setShouldFetchData] = useState(0)
    const debounceTimeoutRef = useRef(null)
    
    // Warehouse and Location state
    const [warehouseMap, setWarehouseMap] = useState({})
    const [locationMap, setLocationMap] = useState({})
    const [selectedWarehouse, setSelectedWarehouse] = useState(searchParams.get('warehouse') || '')
    const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '')
    
    // Destination state
    const [destinationMap, setDestinationMap] = useState({})
    const [subcategoryMap, setSubcategoryMap] = useState({})
    const [selectedDestination, setSelectedDestination] = useState(searchParams.get('destination') || '')
    const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || '')
    
    const [issuedDateRange, setIssuedDateRange] = useState({
        startDate: null,
        endDate: null
    })
    const [displayedDateRange, setDisplayedDateRange] = useState({ startDate: null, endDate: null })
    
    // Return modal state
    const [showReturnModal, setShowReturnModal] = useState(false)
    const [selectedItemForReturn, setSelectedItemForReturn] = useState(null)
    const [returnQuantity, setReturnQuantity] = useState('')
    const [returnWarehouse, setReturnWarehouse] = useState('')
    const [returnLocation, setReturnLocation] = useState('')
    const [returnReason, setReturnReason] = useState('')
    const [returnNotes, setReturnNotes] = useState('')
    const [warehouseOptions, setWarehouseOptions] = useState([])
    const [locationOptions, setLocationOptions] = useState([])
    const [isProcessingReturn, setIsProcessingReturn] = useState(false)
    const [returnReasonOptions, setReturnReasonOptions] = useState([])
    const [returnError, setReturnError] = useState(null)
    
    // Column visibility and widths
    const [columnVisibility, setColumnVisibility] = useState(() => {
        const savedVisibility = localStorage.getItem('issuedInventoryColumnVisibility');
        return savedVisibility ? JSON.parse(savedVisibility) : {
            actions: true,
            date: true,
            issued_item_id: false,
            original_inventory_item_id: false,
            stock_item_gtin: true,
            stock_item_sku: true,
            image: true,
            stock_item_description: true,
            issued_quantity: true,
            issued_by: true,
            destination: true,
            subcategory: true,
            unit_cost: true,
            total_cost: true,
            sell_price: true,
            total_revenue: false,
            profit_per_unit: false,
            total_profit: false,
            original_lot: false,
            original_bb_date: false,
            warehouse: true,
            location: true,
            vendor: false,
            return_status: true,
            returned_quantity: false,
            remaining_returnable: true,
            return_reason: false,
            notes: false
        };
    });

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('issuedInventoryColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            actions: 'auto',
            date: 150,
            issued_item_id: 'auto',
            original_inventory_item_id: 'auto',
            stock_item_gtin: 'auto',
            stock_item_sku: 'auto',
            image: 'auto',
            stock_item_description: 'auto',
            issued_quantity: 'auto',
            issued_by: 'auto',
            destination: 'auto',
            subcategory: 'auto',
            unit_cost: 'auto',
            total_cost: 'auto',
            sell_price: 'auto',
            total_revenue: 'auto',
            profit_per_unit: 'auto',
            total_profit: 'auto',
            original_lot: 'auto',
            original_bb_date: 'auto',
            warehouse: 'auto',
            location: 'auto',
            vendor: 'auto',
            return_status: 'auto',
            returned_quantity: 'auto',
            remaining_returnable: 'auto',
            return_reason: 'auto',
            notes: 'auto'
        };
    });

    const columnLabels = {
        actions: 'Actions',
        date: 'Date',
        issued_item_id: 'Issue ID',
        original_inventory_item_id: 'Original Inv ID',
        stock_item_gtin: 'GTIN',
        stock_item_sku: 'SKU',
        image: 'Image',
        stock_item_description: 'Description',
        issued_quantity: 'Qty Issued',
        issued_by: 'Issued By',
        destination: 'Destination',
        subcategory: 'Destination unit',
        unit_cost: 'Unit Cost',
        total_cost: 'Total Cost',
        sell_price: 'Sell Price',
        total_revenue: 'Total Revenue',
        profit_per_unit: 'Profit/Unit',
        total_profit: 'Total Profit',
        original_lot: 'LOT',
        original_bb_date: 'BB Date',
        warehouse: 'Warehouse',
        location: 'Location',
        vendor: 'Vendor',
        return_status: 'Return Status',
        returned_quantity: 'Qty Returned',
        remaining_returnable: 'Can Return',
        return_reason: 'Return Reason',
        notes: 'Notes'
    };

    // Save column visibility to localStorage
    useEffect(() => {
        localStorage.setItem('issuedInventoryColumnVisibility', JSON.stringify(columnVisibility));
    }, [columnVisibility]);

    // Save column widths to localStorage
    useEffect(() => {
        localStorage.setItem('issuedInventoryColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    // Load warehouses for return modal
    useEffect(() => {
        const loadWarehouses = async () => {
            try {
                const warehouses = await warehouseAPI.getAll();
                // Handle both paginated and non-paginated responses
                const warehouseList = Array.isArray(warehouses) ? warehouses : (warehouses?.results || []);
                setWarehouseOptions(warehouseList.map(wh => ({ 
                    value: wh._id || wh.id, 
                    label: wh.name 
                })));
            } catch (error) {
                console.error('Error loading warehouses:', error);
            }
        };
        loadWarehouses();
    }, []);

    // Load locations when warehouse selected in return modal
    useEffect(() => {
        if (returnWarehouse) {
            const loadLocations = async () => {
                try {
                    const locations = await locationAPI.getAll(returnWarehouse);
                    // Handle both paginated and non-paginated responses
                    const locationList = Array.isArray(locations) ? locations : (locations?.results || []);
                    setLocationOptions(locationList.map(loc => ({ 
                        value: loc._id || loc.id, 
                        label: loc.name 
                    })));
                } catch (error) {
                    console.error('Error loading locations:', error);
                    setLocationOptions([]);
                }
            };
            loadLocations();
        } else {
            setLocationOptions([]);
            setReturnLocation('');
        }
    }, [returnWarehouse]);

    // Fetch issued inventory items
    useEffect(() => {
        const fetchIssuedItems = async () => {
            try {
                setLoading(true);
                const response = await issuedInventoryAPI.getAll(
                    currentPage,
                    pageSize,
                    searchTerm,
                    null, // stockItemId
                    selectedDestination, // parentDestinationId
                    selectedSubcategory, // subcategoryDestinationId
                    null, // issuedBy
                    displayedDateRange.startDate ? displayedDateRange.startDate.toISOString().split('T')[0] : null, // startDate
                    displayedDateRange.endDate ? displayedDateRange.endDate.toISOString().split('T')[0] : null, // endDate
                    null, // isReturned
                    null, // canReturn
                    selectedWarehouse,
                    selectedLocation
                );
                
                setIssuedInventoryItems(response.results || []);
                setTotalPages(response.total_pages || 0);
                setTotalCount(response.count || 0);
            } catch (error) {
                console.error('Error fetching issued inventory items:', error);
                setIssuedInventoryItems([]);
            } finally {
                setLoading(false);
            }
        };

        fetchIssuedItems();
    }, [currentPage, pageSize, searchTerm, shouldFetchData, displayedDateRange, selectedWarehouse, selectedLocation, selectedDestination, selectedSubcategory]);

    // Fetch warehouses
    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const warehouses = await warehouseAPI.getAll();
                // Handle both paginated and non-paginated responses
                const warehouseList = Array.isArray(warehouses) ? warehouses : (warehouses?.results || []);
                const mapping = warehouseList.reduce((acc, warehouse) => {
                    // Handle both _id and id field names
                    const id = warehouse._id || warehouse.id;
                    if (id) {
                        acc[id] = warehouse.name;
                    }
                    return acc;
                }, {});
                setWarehouseMap(mapping);
            } catch (error) {
                console.error('Error fetching warehouses:', error);
            }
        }
        fetchWarehouses();
    }, []);

    // Fetch locations based on selected warehouse
    useEffect(() => {
        async function fetchLocations() {
            try {
                // Only call API if warehouse is valid (not undefined, null, or empty string)
                if (!selectedWarehouse || selectedWarehouse === 'undefined' || selectedWarehouse === 'null') {
                    setLocationMap({});
                    return;
                }
                
                const locations = await locationAPI.getAll(selectedWarehouse);
                
                // Handle both paginated and non-paginated responses
                const locationList = Array.isArray(locations) ? locations : (locations?.results || []);
                const mapping = locationList.reduce((acc, location) => {
                    // Handle both _id and id field names
                    const id = location._id || location.id;
                    if (id) {
                        acc[id] = location.name;
                    }
                    return acc;
                }, {});
                setLocationMap(mapping);
            } catch (error) {
                console.error('Error fetching locations:', error);
                setLocationMap({});
            }
        }
        
        if (selectedWarehouse) {
            fetchLocations();
        } else {
            setLocationMap({});
        }
    }, [selectedWarehouse]);

    // Fetch destinations
    useEffect(() => {
        async function fetchDestinations() {
            try {
                const destinations = await issueDestinationAPI.getAll();
                // Handle both paginated and non-paginated responses
                const destinationList = Array.isArray(destinations) ? destinations : (destinations?.results || []);
                const mapping = destinationList.reduce((acc, destination) => {
                    // Handle both _id and id field names
                    const id = destination._id || destination.id;
                    if (id) {
                        acc[id] = destination.name;
                    }
                    return acc;
                }, {});
                setDestinationMap(mapping);
            } catch (error) {
                console.error('Error fetching destinations:', error);
            }
        }
        fetchDestinations();
    }, []);

    // Fetch return reason choices
    useEffect(() => {
        async function fetchReturnReasonChoices() {
            try {
                const response = await issuedInventoryAPI.getReturnReasonChoices();
                setReturnReasonOptions(response.choices || []);
            } catch (error) {
                console.error('Error fetching return reason choices:', error);
            }
        }
        fetchReturnReasonChoices();
    }, []);

    // Fetch subcategories based on selected destination
    useEffect(() => {
        async function fetchSubcategories() {
            try {
                // Only call API if destination is valid
                if (!selectedDestination || selectedDestination === 'undefined' || selectedDestination === 'null') {
                    setSubcategoryMap({});
                    return;
                }
                
                const subcategories = await issueDestinationAPI.subcategories.getOptions(selectedDestination);
                
                // Handle both paginated and non-paginated responses
                const subcategoryList = Array.isArray(subcategories) ? subcategories : (subcategories?.results || []);
                const mapping = subcategoryList.reduce((acc, subcategory) => {
                    // Handle both _id and id field names
                    const id = subcategory._id || subcategory.id;
                    if (id) {
                        acc[id] = subcategory.name;
                    }
                    return acc;
                }, {});
                setSubcategoryMap(mapping);
            } catch (error) {
                console.error('Error fetching subcategories:', error);
                setSubcategoryMap({});
            }
        }
        
        if (selectedDestination) {
            fetchSubcategories();
        } else {
            setSubcategoryMap({});
        }
    }, [selectedDestination]);

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    // Handle search input change with debounce - matching InventoryListScreen pattern
    const handleSearchInputChange = (e) => {
        const value = e.target.value;

        // Update the input value immediately for responsive UI
        setSearchInputValue(value);

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set a new timeout
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value);
            setCurrentPage(1);
        }, 500); // 500ms debounce
    };

    const toggleColumnVisibility = (column) => {
        setColumnVisibility(prevState => ({
            ...prevState,
            [column]: !prevState[column]
        }));
    };

    // Warehouse and Location filter handlers
    const handleWarehouseFilterChange = (selectedOption) => {
        const warehouseId = selectedOption ? selectedOption.value : '';
        setSelectedWarehouse(warehouseId);
        setSelectedLocation(''); // Clear location when warehouse changes
        setCurrentPage(1);
    };

    const handleLocationFilterChange = (selectedOption) => {
        const locationId = selectedOption ? selectedOption.value : '';
        setSelectedLocation(locationId);
        setCurrentPage(1);
    };

    // Format warehouse options for Select component
    const formatWarehouseOptions = () => {
        return Object.entries(warehouseMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    // Format location options for Select component
    const formatLocationOptions = () => {
        return Object.entries(locationMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    // Destination filter handlers
    const handleDestinationFilterChange = (selectedOption) => {
        const destinationId = selectedOption ? selectedOption.value : '';
        setSelectedDestination(destinationId);
        setSelectedSubcategory(''); // Clear subcategory when destination changes
        setCurrentPage(1);
    };

    const handleSubcategoryFilterChange = (selectedOption) => {
        const subcategoryId = selectedOption ? selectedOption.value : '';
        setSelectedSubcategory(subcategoryId);
        setCurrentPage(1);
    };

    // Format destination options for Select component
    const formatDestinationOptions = () => {
        return Object.entries(destinationMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    // Format subcategory options for Select component
    const formatSubcategoryOptions = () => {
        return Object.entries(subcategoryMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    // Handle return item
    const handleReturnClick = (item) => {
        setSelectedItemForReturn(item);
        setReturnQuantity(item.remaining_returnable_quantity.toString());
        setReturnWarehouse('');
        setReturnLocation('');
        setReturnReason('');
        setReturnNotes('');
        setReturnError(null); // Clear any previous errors
        setShowReturnModal(true);
    };

    const handleReturnSubmit = async (e) => {
        e.preventDefault();
        
        // Clear any previous errors
        setReturnError(null);

        // Validate required fields
        if (!returnWarehouse) {
            setReturnError('Please select a warehouse');
            return;
        }
        
        if (!returnLocation) {
            setReturnError('Please select a location');
            return;
        }
        
        if (!returnNotes.trim()) {
            setReturnError('Please enter return notes');
            return;
        }

        const quantity = parseInt(returnQuantity);
        if (quantity <= 0 || quantity > selectedItemForReturn.remaining_returnable_quantity) {
            setReturnError(`Return quantity must be between 1 and ${selectedItemForReturn.remaining_returnable_quantity}`);
            return;
        }

        try {
            setIsProcessingReturn(true);
            await issuedInventoryAPI.returnItem(selectedItemForReturn._id, {
                quantity,
                warehouse_id: returnWarehouse,
                location_id: returnLocation,
                return_reason: returnReason,
                return_notes: returnNotes
            });
            
            setShowReturnModal(false);
            setShouldFetchData(prev => prev + 1); // Refresh data
        } catch (error) {
            console.error('Error returning item:', error);
            setReturnError('Failed to return item. Please try again.');
        } finally {
            setIsProcessingReturn(false);
        }
    };

    // Render return status badge
    const renderReturnStatus = (item) => {
        if (!item.is_returned) {
            return <span className="badge bg-secondary">Not Returned</span>;
        } else if (item.is_fully_returned) {
            return <span className="badge bg-success">Fully Returned</span>;
        } else {
            return <span className="badge bg-warning">Partially Returned</span>;
        }
    };

    // Column definitions for the table - using same structure as InventoryListScreen
    const columns = [
        // Date column first with filtering dropdown - using reusable component
        {
            id: 'date',
            header: () => (
                <DateFilterDropdown
                    dateRange={issuedDateRange}
                    setDateRange={setIssuedDateRange}
                    displayedDateRange={displayedDateRange}
                    setDisplayedDateRange={setDisplayedDateRange}
                    onApply={() => setShouldFetchData(prev => prev + 1)}
                    dropdownId="issued-date-filter-dropdown"
                />
            ),
            cell: (item) => formatDateTime(item.issued_date)
        },
        ...(columnVisibility.issued_item_id ? [{
            id: 'issued_item_id',
            header: columnLabels.issued_item_id,
            cell: (item) => item._id
        }] : []),
        ...(columnVisibility.original_inventory_item_id ? [{
            id: 'original_inventory_item_id',
            header: columnLabels.original_inventory_item_id,
            cell: (item) => item.original_inventory_item_id
        }] : []),
        ...(columnVisibility.stock_item_gtin ? [{
            id: 'stock_item_gtin',
            header: columnLabels.stock_item_gtin,
            cell: (item) => highlightText(item.stock_item_gtin || '', searchTerm)
        }] : []),
        ...(columnVisibility.stock_item_sku ? [{
            id: 'stock_item_sku',
            header: columnLabels.stock_item_sku,
            cell: (item) => highlightText(item.stock_item_sku || '', searchTerm)
        }] : []),
        ...(columnVisibility.image ? [{
            id: 'image',
            header: columnLabels.image,
            cell: (item) => item.stock_item_image ? (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip className="stock-item-tooltip">
                            <StockItemInfo stockItem={{ 
                                _id: item.stock_item,
                                gtin: item.stock_item_gtin,
                                description: item.stock_item_description,
                                image: item.stock_item_image
                            }} />
                        </Tooltip>
                    }
                >
                    <img 
                        src={item.stock_item_image} 
                        alt="Stock Item Thumbnail"
                        style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                    />
                </OverlayTrigger>
            ) : null
        }] : []),
        ...(columnVisibility.stock_item_description ? [{
            id: 'stock_item_description',
            header: columnLabels.stock_item_description,
            cell: (item) => highlightText(item.stock_item_description || '', searchTerm)
        }] : []),
        ...(columnVisibility.issued_quantity ? [{
            id: 'issued_quantity',
            header: columnLabels.issued_quantity,
            cell: (item) => formatQuantity(item.issued_quantity)
        }] : []),
        ...(columnVisibility.issued_by ? [{
            id: 'issued_by',
            header: columnLabels.issued_by,
            cell: (item) => item.issued_by_username || 'Unknown'
        }] : []),
        ...(columnVisibility.destination ? [{
            id: 'destination',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedDestination ? { value: selectedDestination, label: destinationMap[selectedDestination] } : null}
                        onChange={handleDestinationFilterChange}
                        options={formatDestinationOptions()}
                        isSearchable
                        isClearable
                        placeholder="Destination"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            ),
            cell: (item) => {
                // Use the properly separated destination name from the server
                const destinationName = item.issue_destination_name || '';
                return highlightText(destinationName, searchTerm);
            }
        }] : []),
        ...(columnVisibility.subcategory ? [{
            id: 'subcategory',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedSubcategory ? { value: selectedSubcategory, label: subcategoryMap[selectedSubcategory] } : null}
                        onChange={handleSubcategoryFilterChange}
                        options={formatSubcategoryOptions()}
                        isSearchable
                        isClearable
                        placeholder="Destination unit"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!selectedDestination}
                    />
                </div>
            ),
            cell: (item) => {
                // Use the properly separated subcategory name from the server
                const subcategoryName = item.issue_destination_subcategory_name || '';
                return highlightText(subcategoryName, searchTerm);
            }
        }] : []),
        ...(columnVisibility.unit_cost ? [{
            id: 'unit_cost',
            header: columnLabels.unit_cost,
            cell: (item) => item.unit_cost ? formatCurrency(item.unit_cost) : '-'
        }] : []),
        ...(columnVisibility.total_cost ? [{
            id: 'total_cost',
            header: columnLabels.total_cost,
            cell: (item) => item.total_cost ? formatCurrency(item.total_cost) : '-'
        }] : []),
        ...(columnVisibility.sell_price ? [{
            id: 'sell_price',
            header: columnLabels.sell_price,
            cell: (item) => item.sell_price ? formatCurrency(item.sell_price) : '-'
        }] : []),
        ...(columnVisibility.total_revenue ? [{
            id: 'total_revenue',
            header: columnLabels.total_revenue,
            cell: (item) => item.total_revenue ? formatCurrency(item.total_revenue) : '-'
        }] : []),
        ...(columnVisibility.profit_per_unit ? [{
            id: 'profit_per_unit',
            header: columnLabels.profit_per_unit,
            cell: (item) => {
                if (item.profit_per_unit !== null && item.profit_per_unit !== undefined) {
                    const color = item.profit_per_unit >= 0 ? 'text-success' : 'text-danger';
                    return <span className={color}>{formatCurrency(item.profit_per_unit)}</span>;
                }
                return '-';
            }
        }] : []),
        ...(columnVisibility.total_profit ? [{
            id: 'total_profit',
            header: columnLabels.total_profit,
            cell: (item) => {
                if (item.total_profit !== null && item.total_profit !== undefined) {
                    const color = item.total_profit >= 0 ? 'text-success' : 'text-danger';
                    return <span className={color}>{formatCurrency(item.total_profit)}</span>;
                }
                return '-';
            }
        }] : []),
        ...(columnVisibility.original_lot ? [{
            id: 'original_lot',
            header: columnLabels.original_lot,
            cell: (item) => item.original_lot || '-'
        }] : []),
        ...(columnVisibility.original_bb_date ? [{
            id: 'original_bb_date',
            header: columnLabels.original_bb_date,
            cell: (item) => item.original_bb_date ? formatDate(item.original_bb_date) : '-'
        }] : []),
        ...(columnVisibility.warehouse ? [{
            id: 'warehouse',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedWarehouse ? { value: selectedWarehouse, label: warehouseMap[selectedWarehouse] } : null}
                        onChange={handleWarehouseFilterChange}
                        options={formatWarehouseOptions()}
                        isSearchable
                        isClearable
                        placeholder="Warehouse"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
            ),
            cell: (item) => (
                <div className="d-flex flex-column">
                    <span>{item.warehouse_name_snapshot}</span>
                </div>
            )
        }] : []),
        ...(columnVisibility.location ? [{
            id: 'location',
            header: () => (
                <div style={{ minWidth: '200px' }} className="compact-select">
                    <Select
                        value={selectedLocation ? { value: selectedLocation, label: locationMap[selectedLocation] } : null}
                        onChange={handleLocationFilterChange}
                        options={formatLocationOptions()}
                        isSearchable
                        isClearable
                        placeholder="Location"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!selectedWarehouse}
                    />
                </div>
            ),
            cell: (item) => (
                <div className="d-flex flex-column">
                    <span>{item.location_name_snapshot}</span>
                </div>
            )
        }] : []),
        ...(columnVisibility.vendor ? [{
            id: 'vendor',
            header: columnLabels.vendor,
            cell: (item) => item.vendor_name_snapshot || '-'
        }] : []),
        // RETURN-RELATED COLUMNS
        ...(columnVisibility.return_status ? [{
            id: 'return_status',
            header: columnLabels.return_status,
            cell: (item) => renderReturnStatus(item)
        }] : []),
        ...(columnVisibility.returned_quantity ? [{
            id: 'returned_quantity',
            header: columnLabels.returned_quantity,
            cell: (item) => formatQuantity(item.returned_quantity) || '0'
        }] : []),
        ...(columnVisibility.remaining_returnable ? [{
            id: 'remaining_returnable',
            header: columnLabels.remaining_returnable,
            cell: (item) => formatQuantity(item.remaining_returnable_quantity) || '0'
        }] : []),
        ...(columnVisibility.return_reason ? [{
            id: 'return_reason',
            header: columnLabels.return_reason,
            cell: (item) => {
                if (!item.return_reason) return '-';
                const reasonOption = returnReasonOptions.find(opt => opt.value === item.return_reason);
                return reasonOption ? reasonOption.label : item.return_reason;
            }
        }] : []),
        ...(columnVisibility.notes ? [{
            id: 'notes',
            header: columnLabels.notes,
            cell: (item) => highlightText(item.notes || '', searchTerm)
        }] : []),
        // ACTIONS COLUMN (RETURN BUTTON)
        ...(columnVisibility.actions ? [{
            id: 'actions',
            header: columnLabels.actions,
            cell: (item) => (
                <div>
                    {item.can_return && (
                        <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleReturnClick(item)}
                            className="me-1"
                        >
                            Return
                        </Button>
                    )}
                </div>
            )
        }] : [])
    ];

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3 align-items-center">
                <Col md={4}>
                    <h1>Issued Inventory</h1>
                </Col>
                <Col md={4}>
                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onClear={handleClearSearch}
                        placeholder="Search by any field"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
            </Row>

            <ResizableTable
                tableId="issued_inventory_table"
                columns={columns}
                data={issuedInventoryItems}
            />

            {/* Pagination */}
            <PaginationComponent
                currentPage={currentPage}
                totalItems={totalCount}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setPageSize}
            />

            {/* Return Modal */}
            <Modal show={showReturnModal} onHide={() => { setShowReturnModal(false); setReturnError(null); }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Return Issued Item</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleReturnSubmit}>
                    <Modal.Body>
                        {selectedItemForReturn && (
                            <>
                                <div className="mb-3">
                                    <strong>Item:</strong> {selectedItemForReturn.stock_item_description}<br/>
                                    <strong>SKU:</strong> {selectedItemForReturn.stock_item_sku}<br/>
                                    <strong>Issued Quantity:</strong> {formatQuantity(selectedItemForReturn.issued_quantity)}<br/>
                                    <strong>Available to Return:</strong> {formatQuantity(selectedItemForReturn.remaining_returnable_quantity)}
                                </div>

                                {returnError && (
                                    <Alert variant="danger" onClose={() => setReturnError(null)} dismissible>
                                        {returnError}
                                    </Alert>
                                )}
                                
                                <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Return Quantity *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max={selectedItemForReturn.remaining_returnable_quantity}
                                            value={returnQuantity}
                                            onChange={(e) => setReturnQuantity(e.target.value)}
                                            required
                                            placeholder="Enter quantity"
                                            autoComplete="off"
                                            spellCheck="false"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Return to Warehouse *</Form.Label>
                                        <Select
                                            value={warehouseOptions.find(opt => opt.value === returnWarehouse)}
                                            onChange={(option) => setReturnWarehouse(option ? option.value : '')}
                                            options={warehouseOptions}
                                            placeholder="Select warehouse..."
                                            isClearable
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Return to Location *</Form.Label>
                                        <Select
                                            value={locationOptions.find(opt => opt.value === returnLocation)}
                                            onChange={(option) => setReturnLocation(option ? option.value : '')}
                                            options={locationOptions}
                                            placeholder="Select location..."
                                            isDisabled={!returnWarehouse}
                                            isClearable
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Return Reason</Form.Label>
                                        <Select
                                            value={returnReasonOptions.find(opt => opt.value === returnReason)}
                                            onChange={(option) => setReturnReason(option ? option.value : '')}
                                            options={returnReasonOptions}
                                            placeholder="Select return reason..."
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Return Notes *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={returnNotes}
                                    onChange={(e) => setReturnNotes(e.target.value)}
                                    placeholder="Enter return notes (required)..."
                                    required
                                    autoComplete="off"
                                    spellCheck="true"
                                />
                            </Form.Group>
                        </>
                    )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => { setShowReturnModal(false); setReturnError(null); }}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit"
                            disabled={isProcessingReturn}
                        >
                            {isProcessingReturn ? 'Processing...' : 'Return Item'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}

export default IssuedInventoryListScreen;